import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { usePermissions } from './hooks/usePermissions';
import { DashboardProvider } from './context/DashboardContext';
import ErrorBoundary from './components/ErrorBoundary';
import LazyLoadErrorBoundary from './components/LazyLoadErrorBoundary';
import EnvironmentWrapper from './components/EnvironmentWrapper';
import { DebugProvider } from './context/DebugContext';
import DebugWidget from './components/DebugWidget/DebugWidget';
import CsfLoading from './components/CsfLoading';

// Layout components (keep these as direct imports for critical path)
import Layout from './components/Layout';
import PublicHelpLayout from './components/PublicHelpLayout';

// Lazy load page components for code splitting
// Task Management
const TasksPage = lazy(() => import('./pages/Tasks/TasksPage'));
const TaskFormPage = lazy(() => import('./pages/Tasks/TaskFormPage'));
const MaintenanceTaskFormPage = lazy(() => import('./pages/Tasks/MaintenanceTaskFormPage'));
const TaskDetailPage = lazy(() => import('./pages/Tasks/TaskDetailPage'));
const RecurringTasksPage = lazy(() => import('./pages/Tasks/RecurringTasksPage'));

// Ticket Management
const TicketsPage = lazy(() => import('./pages/Tickets/TicketsPage'));
const TicketDetailPage = lazy(() => import('./pages/Tickets/TicketDetailPage'));
const TicketFormPage = lazy(() => import('./pages/Tickets/TicketFormPage'));
const TicketCategoriesAndTagsPage = lazy(() => import('./pages/Tickets/TicketCategoriesAndTagsPage'));

// Core Page components
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ShortcutsPage = lazy(() => import('./pages/ShortcutsPage'));
const DriveFilesPage = lazy(() => import('./pages/DriveFilesPage'));
const DriveViewerPage = lazy(() => import('./pages/DriveViewerPage'));
const UnifiedUserManagementPage = lazy(() => import('./pages/UnifiedUserManagementPage'));
const UnifiedUserProfilePage = lazy(() => import('./pages/UnifiedUserProfilePage'));
const ServiceUsersPage = lazy(() => import('./components/ServiceUsers/ServiceUsersPage'));
const AdminRolesPage = lazy(() => import('./pages/AdminRolesPage'));
const AdminAccessRequestsPage = lazy(() => import('./pages/AdminAccessRequestsPage'));
const AdminShortcutsPage = lazy(() => import('./pages/AdminShortcutsPage'));
const ProvisioningManagementPage = lazy(() => import('./pages/Admin/ProvisioningManagementPage'));
const AdminMenuPage = lazy(() => import('./pages/Admin/AdminMenuPage'));
const KiosksAdminPage = lazy(() => import('./pages/Admin/KiosksAdminPage'));
const HelpAdminPage = lazy(() => import('./pages/Admin/HelpAdminPage'));
const FinanceCategoriesAdminPage = lazy(() => import('./pages/Admin/FinanceCategoriesAdminPage'));
const FinanceAccountsAdminPage = lazy(() => import('./pages/Admin/FinanceAccountsAdminPage'));
const SystemStatusPage = lazy(() => import('./pages/Admin/SystemStatusPage'));
const ApiDebugPage = lazy(() => import('./pages/Admin/ApiDebugPage'));
const EmailDebugPage = lazy(() => import('./pages/Admin/EmailDebugPage'));
const CodeConsolePage = lazy(() => import('./pages/Admin/CodeConsolePage'));
const OcrMonitoringPage = lazy(() => import('./pages/Admin/OcrMonitoringPage'));
const OcrTrainingPage = lazy(() => import('./pages/Admin/OcrTrainingPage'));
const IntegrationSyncManagement = lazy(() => import('./pages/Admin/IntegrationSyncManagement'));
const SystemMonitoring = lazy(() => import('./pages/Admin/SystemMonitoring'));
const ActivityLogsPage = lazy(() => import('./pages/Admin/ActivityLogsPage'));
const AuditLogsPage = lazy(() => import('./pages/Admin/AuditLogsPage'));
const AmazonAdminPage = lazy(() => import('./pages/Admin/AmazonAdminPage'));
const AlertsConfigPage = lazy(() => import('./pages/Admin/AlertsConfigPage'));
const DatabaseMigrationPage = lazy(() => import('./pages/Admin/DatabaseMigrationPage'));
const SearchIndexPage = lazy(() => import('./pages/Admin/SearchIndexPage'));
const SnmpPage = lazy(() => import('./pages/SnmpPage'));
const Namecheap = lazy(() => import('./pages/Namecheap'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const SetupTwoFactorPage = lazy(() => import('./pages/Auth/SetupTwoFactorPage'));
const VerifyTwoFactorPage = lazy(() => import('./pages/Auth/VerifyTwoFactorPage'));
const VerifyEmailTwoFactorPage = lazy(() => import('./pages/Auth/VerifyEmailTwoFactorPage'));
const TotpTokensPage = lazy(() => import('./pages/TotpTokensPage'));
const TotpScanPage = lazy(() => import('./pages/TotpScanPage'));
const ReceiptTestingPage = lazy(() => import('./pages/ReceiptTestingPage'));

// Help and FAQ pages
const HelpPage = lazy(() => import('./pages/Help/HelpPage'));
const FAQPage = lazy(() => import('./pages/Help/FAQPage'));

// Public Help pages
const PublicHelpPage = lazy(() => import('./pages/PublicHelp/PublicHelpPage'));
const PublicHelpArticle = lazy(() => import('./pages/PublicHelp/PublicHelpArticle'));

// Public Asset View
const PublicAssetView = lazy(() => import('./pages/PublicAssetView'));
const KioskPage = lazy(() => import('./pages/KioskPage'));

// Team Help Management
const TeamHelpManagement = lazy(() => import('./pages/Teams/TeamHelpManagement'));

// Getting Started help pages
const LoggingInPage = lazy(() => import('./pages/Help/GettingStarted/LoggingInPage'));
const NavigationPage = lazy(() => import('./pages/Help/GettingStarted/NavigationPage'));
const YourProfilePage = lazy(() => import('./pages/Help/GettingStarted/YourProfilePage'));

// Core Features help pages
const DashboardHelpPage = lazy(() => import('./pages/Help/CoreFeatures/DashboardPage'));
const ShortcutsHelpPage = lazy(() => import('./pages/Help/CoreFeatures/ShortcutsPage'));
const DriveFilesHelpPage = lazy(() => import('./pages/Help/CoreFeatures/DriveFilesPage'));
const CalendarHelpPage = lazy(() => import('./pages/Help/CoreFeatures/CalendarPage'));
const StaffDirectoryHelpPage = lazy(() => import('./pages/Help/CoreFeatures/StaffDirectoryPage'));

// Integrations help pages
const CanvaHelpPage = lazy(() => import('./pages/Help/Integrations/CanvaPage'));
const PlanningCenterHelpPage = lazy(() => import('./pages/Help/Integrations/PlanningCenterPage'));
const SynologyHelpPage = lazy(() => import('./pages/Help/Integrations/SynologyPage'));
const DreoHelpPage = lazy(() => import('./pages/Help/Integrations/DreoPage'));
const UnifiAccessHelpPage = lazy(() => import('./pages/Help/Integrations/UnifiAccessPage'));
const GoogleDriveHelpPage = lazy(() => import('./pages/Help/Integrations/GoogleDrivePage'));
const GoogleCalendarHelpPage = lazy(() => import('./pages/Help/Integrations/GoogleCalendarPage'));
const GoogleFormsHelpPage = lazy(() => import('./pages/Help/Integrations/GoogleFormsPage'));
const GoogleAdminHelpPage = lazy(() => import('./pages/Help/Integrations/GoogleAdminPage'));
const LenelS2NetBoxHelpPage = lazy(() => import('./pages/Help/Integrations/LenelS2NetBoxPage'));
const MosyleBusinessHelpPage = lazy(() => import('./pages/Help/Integrations/MosyleBusinessPage'));
const UnifiNetworkHelpPage = lazy(() => import('./pages/Help/Integrations/UnifiNetworkPage'));
const UnifiProtectHelpPage = lazy(() => import('./pages/Help/Integrations/UnifiProtectPage'));
const RadiusHelpPage = lazy(() => import('./pages/Help/Integrations/RadiusPage'));

// Staff Documents page
const StaffDocumentsPage = lazy(() => import('./pages/StaffDocuments/StaffDocumentsPage'));

// Staff Directory pages
const StaffDirectoryPage = lazy(() => import('./pages/StaffDirectory/StaffDirectoryPage'));
const UserProfilePage = lazy(() => import('./pages/StaffDirectory/UserProfilePage'));
const TeamManagementPage = lazy(() => import('./pages/StaffDirectory/TeamManagementPage'));
const TeamsHomePage = lazy(() => import('./pages/Teams/TeamsHomePage'));
const GroupManagementPage = lazy(() => import('./pages/StaffDirectory/GroupManagementPage'));

// Integration pages
const CanvaPage = lazy(() => import('./pages/Canva/CanvaPage'));
const CanvaSetup = lazy(() => import('./pages/Canva/CanvaSetup'));
const PlanningCenterPage = lazy(() => import('./pages/PlanningCenter/PlanningCenterPage'));
const SynologyFileBrowser = lazy(() => import('./pages/Synology/SynologyFileBrowser'));
const SynologySetup = lazy(() => import('./pages/Synology/SynologySetup'));

// New integration pages
const DreoPage = lazy(() => import('./pages/Dreo/DreoPage'));
const LGThinqPage = lazy(() => import('./pages/LGThinq/LGThinqPage'));
const RainBirdPage = lazy(() => import('./pages/RainBird/RainBirdPage'));
const RainBirdSetup = lazy(() => import('./pages/RainBird/RainBirdSetup'));
const AmazonPage = lazy(() => import('./pages/Amazon/AmazonPage'));


// Additional integration and utility pages
const LenelS2NetBoxPage = lazy(() => import('./pages/LenelS2NetBox/LenelS2NetBoxPage'));
const DoorSchedulesPage = lazy(() => import('./pages/DoorSchedules/DoorSchedulesPage'));
const BulkAddPeoplePage = lazy(() => import('./pages/DoorSchedules/BulkAddPeoplePage'));
const CloudflarePage = lazy(() => import('./pages/Cloudflare/CloudflarePage'));
const TeamHubPage = lazy(() => import('./pages/TeamHub/TeamHubPage'));
const TeamHubsIndexPage = lazy(() => import('./pages/TeamHub/TeamHubsIndexPage'));
const MosyleBusinessPage = lazy(() => import('./pages/MosyleBusiness/MosyleBusinessPage'));
const UnifiAccessPage = lazy(() => import('./pages/UnifiAccess/UnifiAccessPage'));
const UnifiedPeoplePage = lazy(() => import('./pages/People/UnifiedPeoplePage'));
const GoogleAdminPage = lazy(() => import('./pages/GoogleAdmin/GoogleAdminPage'));
const GoogleAdminUsersPage = lazy(() => import('./pages/GoogleAdmin/GoogleAdminUsersPage'));
const GoogleAdminGroupsPage = lazy(() => import('./pages/GoogleAdmin/GoogleAdminGroupsPage'));
const GoogleCalendarPage = lazy(() => import('./pages/GoogleCalendar/GoogleCalendarPage'));
const GoogleFormsPage = lazy(() => import('./pages/GoogleForms/GoogleFormsPage'));
const GoogleFormsWebhookPage = lazy(() => import('./pages/GoogleForms/Webhooks/GoogleFormsWebhookPage'));
const GoogleConfigEnvMessage = lazy(() => import('./pages/GoogleConfigEnvMessage'));
const UnifiNetworkPage = lazy(() => import('./pages/UnifiNetwork/UnifiNetworkPage'));
const UnifiProtectPage = lazy(() => import('./pages/UnifiProtect/UnifiProtectPage'));
const UnifiSuperlinkPage = lazy(() => import('./pages/UnifiSuperlink/UnifiSuperlinkPage'));
const AppleBusinessManagerPage = lazy(() => import('./pages/AppleBusinessManager/AppleBusinessManagerPage'));
const RoomBookingPage = lazy(() => import('./pages/RoomBooking/RoomBookingPage'));
const SuppliesPage = lazy(() => import('./pages/Supplies/SuppliesPage'));
const SensorsPage = lazy(() => import('./pages/Sensors/SensorsPage'));
const ParkingPassManagement = lazy(() => import('./components/ParkingPass/ParkingPassManagement'));
const BuildingManagementAdminPage = lazy(() => import('./pages/Admin/BuildingManagementAdminPageNew'));
const PhoneBookPage = lazy(() => import('./pages/PhoneBook/PhoneBookPage'));
const RadiusPage = lazy(() => import('./pages/Radius/RadiusPage'));
const RadiusSetup = lazy(() => import('./pages/Radius/RadiusSetup'));
const CertificateManagementPage = lazy(() => import('./pages/Radius/CertificateManagementPage'));
const WiimPage = lazy(() => import('./pages/Wiim/WiimPage'));
const AccessControlPage = lazy(() => import('./pages/AccessControl/AccessControlPage'));
const UnifiedAccessControlPage = lazy(() => import('./pages/AccessControl/UnifiedAccessControlPage'));
const UnifiAccessUsersPage = lazy(() => import('./components/UnifiAccess/UnifiAccessUsers'));
const UnifiAccessLogsPage = lazy(() => import('./components/UnifiAccess/UnifiAccessLogs'));
const WiimSetup = lazy(() => import('./pages/Wiim/WiimSetup'));
const SkyportCloudPage = lazy(() => import('./pages/SkyportCloud/SkyportCloudPage'));
const QsysPage = lazy(() => import('./pages/Qsys/QsysPageEnhanced'));
const CueServerPage = lazy(() => import('./pages/CueServer/CueServerPage'));
const BlackmagicPage = lazy(() => import('./pages/Blackmagic/BlackmagicPage'));
const BlackmagicSetup = lazy(() => import('./pages/Blackmagic/BlackmagicSetup'));
const ProPresenterPage = lazy(() => import('./pages/ProPresenter/ProPresenterPage'));
const ProjectorsPage = lazy(() => import('./pages/Projectors/ProjectorsPage'));
const TVsPage = lazy(() => import('./pages/TVs/TVsPage'));
const ProductionDashboard = lazy(() => import('./pages/Production/ProductionDashboard'));
const YamahaTfPage = lazy(() => import('./pages/YamahaTf/YamahaTfPage'));
const SpeakerZonesPage = lazy(() => import('./pages/LeaAudio/SpeakerZonesPage'));
const PowerPage = lazy(() => import('./pages/Power/PowerPage'));
const CanonPage = lazy(() => import('./pages/Canon/CanonPage'));
const CiscoSwitchPage = lazy(() => import('./pages/Cisco/CiscoSwitchPage'));
const GoveePage = lazy(() => import('./pages/Govee/GoveePage'));
const ConstantContactPage = lazy(() => import('./pages/ConstantContact/ConstantContactPage'));
const PrintServerDashboard = lazy(() => import('./components/PrintServer/PrintServerDashboard'));
const PrintersPage = lazy(() => import('./components/PrintServer/PrintersPage'));
const UnifiedUserManagement = lazy(() => import('./pages/UnifiedUserManagement'));

// QuickBooks imports
const QuickBooksPage = lazy(() => import('./pages/QuickBooks/QuickBooksPage'));
const ReceiptReconciliationPage = lazy(() => import('./pages/QuickBooks/ReceiptReconciliationPage'));
const TransactionsPage = lazy(() => import('./pages/QuickBooks/TransactionsPage'));

// BMS imports
const BMSPage = lazy(() => import('./pages/BMS/BMSPage'));

// Form Builder imports
const FormBuilderPage = lazy(() => import('./pages/Forms/FormBuilderPage'));
const FormsListPage = lazy(() => import('./pages/Forms/FormsListPage'));
const FormViewPage = lazy(() => import('./pages/Forms/FormViewPage'));
const MyFormsPage = lazy(() => import('./pages/Forms/MyFormsPage'));
const FormSubmissionsPage = lazy(() => import('./pages/Forms/FormSubmissionsPage'));

// Asset Management pages
const AssetRequestsPage = lazy(() => import('./pages/AssetManagement/AssetRequestsPage'));
const AssetIssuesPage = lazy(() => import('./pages/AssetManagement/AssetIssuesPage'));
const AssetReportsPage = lazy(() => import('./pages/Assets/AssetReportsPage'));
const AssetImportExportPage = lazy(() => import('./pages/Assets/AssetImportExportPage'));
const AssetListPage = lazy(() => import('./pages/Assets/AssetListPage'));
const AssetFormPage = lazy(() => import('./pages/Assets/AssetFormPage'));
const AssetViewPage = lazy(() => import('./pages/Assets/AssetViewPage'));
const AssetHistoryPage = lazy(() => import('./pages/Assets/AssetHistoryPage'));
const AssetDashboard = lazy(() => import('./pages/Assets/AssetDashboard'));

// Notes page
const NotesPage = lazy(() => import('./pages/Notes/NotesPage'));

// Receipt and Purchase Management pages
const ReceiptManagementPage = lazy(() => import('./pages/Receipts/ReceiptManagementPage'));
const PublicReceiptUpdate = lazy(() => import('./components/Receipts/PublicReceiptUpdate'));
const PurchaseRequestPage = lazy(() => import('./pages/PurchaseRequests/PurchaseRequestPage'));

// Procurement System pages
const ProcurementDashboard = lazy(() => import('./components/Procurement/ProcurementDashboard'));
const VendorManagement = lazy(() => import('./components/Procurement/VendorManagement'));
const OrderManagement = lazy(() => import('./components/Procurement/OrderManagement'));
const AmazonPunchoutConfig = lazy(() => import('./components/Procurement/AmazonPunchoutConfig'));
const AmazonShopping = lazy(() => import('./components/Procurement/AmazonShopping'));
const ProcurementAnalytics = lazy(() => import('./components/Procurement/ProcurementAnalytics'));
const PurchasingGroups = lazy(() => import('./components/Procurement/PurchasingGroups'));
const DocumentCollaboration = lazy(() => import('./components/Procurement/DocumentCollaboration'));
const ProcurementSettings = lazy(() => import('./components/Procurement/ProcurementSettings'));

// License Manager
const LicenseManagerPage = lazy(() => import('./pages/Licenses/LicenseManagerPage'));
const LicenseBrowser = lazy(() => import('./pages/Licenses/components/LicenseBrowser'));
const MyLicenses = lazy(() => import('./pages/Licenses/components/MyLicenses'));
const LicenseRequests = lazy(() => import('./pages/Licenses/components/LicenseRequests'));
const AdminLicenseManagement = lazy(() => import('./pages/Licenses/components/AdminLicenseManagement'));

// News pages
const NewsPage = lazy(() => import('./pages/News/NewsPage'));
const NewsPostPage = lazy(() => import('./pages/News/NewsPostPage'));
const NewsPostForm = lazy(() => import('./pages/News/NewsPostForm'));

// Meeting Management pages
const MeetingsListPage = lazy(() => import('./pages/Meetings/MeetingsListPage'));
const MeetingDetailPage = lazy(() => import('./pages/Meetings/MeetingDetailPage'));
const MeetingFormPage = lazy(() => import('./pages/Meetings/MeetingFormPage'));
const PublicMeetingPage = lazy(() => import('./pages/Meetings/PublicMeetingPage'));
const AgendaBuilderPage = lazy(() => import('./pages/Meetings/AgendaBuilderPage'));
const CommitteesListPage = lazy(() => import('./pages/Meetings/CommitteesListPage'));
const CommitteeFormPage = lazy(() => import('./pages/Meetings/CommitteeFormPage'));
const CommitteeDetailPage = lazy(() => import('./pages/Meetings/CommitteeDetailPage'));

// Debug widget
// DebugWidget removed for production

// Redirect component to unify Building Management to BMS
const RedirectToBMS = () => {
  const location = useLocation();
  // Replace only the first segment 'building-management' with 'bms'
  const newPath = location.pathname.replace(/^(\/?)building-management/, '$1bms');
  return <Navigate to={newPath} replace />;
};

// Protected route component
const ProtectedRoute = ({ children, requiredRoles = [], requiredPermission = null }) => {
  const { user, loading, isAdmin } = useAuth();
  const { hasIntegrationPermission } = usePermissions();

  // Show loading indicator while checking authentication
  if (loading) {
    return <CsfLoading fullScreen />;
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Enforce 2FA setup for local users before accessing any protected route
  if (user.authType === 'local' && !user.twoFactorSetupComplete) {
    return <Navigate to="/setup-2fa" replace />;
  }

  // If roles are required, check if user has at least one of them
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => user.roles.includes(role));
    if (!hasRequiredRole && !isAdmin()) {
      return <Navigate to="/dashboard" />;
    }
  }

  // If integration permission is required, check it
  if (requiredPermission) {
    if (requiredPermission.includes(':')) {
      const [integration, action] = requiredPermission.split(':');
      if (!hasIntegrationPermission(integration, action)) {
        return <Navigate to="/dashboard" />;
      }
    }
  }

  return children;
};

// Root redirect: send logged-out users to login, logged-in users to dashboard
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return <CsfLoading fullScreen />;
  }
  return <Navigate to={user ? '/dashboard' : '/login'} />;
};

// Profile redirect: send users to their own profile page
const ProfileRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return <CsfLoading fullScreen />;
  }
  if (!user) {
    return <Navigate to="/login" />;
  }
  return <Navigate to={`/staff-directory/users/${user._id}?tab=profile`} />;
};


function App() {
  const AppContent = () => (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<CsfLoading fullScreen />}>
        <Routes>
        {/* Public Help System - using standalone layout */}
        <Route path="/public-help" element={<PublicHelpLayout />}>
          <Route index element={<PublicHelpPage />} />
          <Route path="category/:categorySlug" element={<PublicHelpPage />} />
          <Route path="article/:articleId" element={<PublicHelpArticle />} />
        </Route>

        {/* Lobby kiosk — unauthenticated by design, and deliberately outside
            Layout so it has no portal chrome and no session to escape into.
            The :token resolves server-side to one building+floor. */}
        <Route path="/kiosk/:token" element={<KioskPage />} />

        <Route path="/" element={<Layout />}>
        {/* Public routes */}
        <Route index element={<RootRedirect />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="terms" element={<TermsOfService />} />
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="asset/:id" element={<PublicAssetView />} />
        <Route path="receipts/update/:token" element={<PublicReceiptUpdate />} />
        <Route path="meetings/access/:token" element={<PublicMeetingPage />} />
        <Route path="setup-2fa" element={<SetupTwoFactorPage />} />
        <Route path="verify-2fa/:userId" element={<VerifyTwoFactorPage />} />
        <Route path="verify-email-2fa/:userId" element={<VerifyEmailTwoFactorPage />} />
        <Route path="scan/:sessionId" element={<TotpScanPage />} />
        <Route path="receipttesting" element={<ReceiptTestingPage />} />

        {/* Protected routes */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardProvider>
                <DashboardPage />
              </DashboardProvider>
            </ProtectedRoute>
          }
        />

        <Route
          path="shortcuts"
          element={
            <ProtectedRoute>
              <ShortcutsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfileRedirect />
            </ProtectedRoute>
          }
        />

        {/* Apps route removed - now using modal in header */}

        <Route 
          path="drive" 
          element={
            <ProtectedRoute requiredPermission="googleDrive:read">
              <DriveFilesPage />
            </ProtectedRoute>
          } 
        />

        <Route
          path="drive/view/:fileId"
          element={
            <ProtectedRoute requiredPermission="googleDrive:read">
              <DriveViewerPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="staff-documents"
          element={
            <ProtectedRoute>
              <StaffDocumentsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="room-booking"
          element={
            <ProtectedRoute>
              <RoomBookingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="supplies"
          element={
            <ProtectedRoute>
              <SuppliesPage />
            </ProtectedRoute>
          }
        />

        {/* Sensors across every integration — the one place to classify and
            alias them. Not HVAC-specific by design. */}
        <Route
          path="sensors"
          element={
            <ProtectedRoute requiredPermission="bms:read">
              <SensorsPage />
            </ProtectedRoute>
          }
        />

        {/* Buildings, floors, rooms and floorplans. Lives under Building
            Management rather than Admin: staff who look after rooms need it,
            and it was only ever admin-gated by its menu placement. Every
            mutation still goes through buildings:write / floors:write /
            rooms:write on the API, so a read-level user can look and not
            touch. */}
        <Route
          path="buildings"
          element={
            <ProtectedRoute requiredPermission="floors:read">
              <BuildingManagementAdminPage />
            </ProtectedRoute>
          }
        />
        {/* Building detail and its sections are real URLs, so a refresh lands
            back where you were. */}
        <Route
          path="buildings/:buildingId"
          element={
            <ProtectedRoute requiredPermission="floors:read">
              <BuildingManagementAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="buildings/:buildingId/:section"
          element={
            <ProtectedRoute requiredPermission="floors:read">
              <BuildingManagementAdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="snmp"
          element={
            <ProtectedRoute>
              <SnmpPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="snmp/:tab"
          element={
            <ProtectedRoute>
              <SnmpPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="namecheap"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <Namecheap />
            </ProtectedRoute>
          }
        />

        <Route
          path="subscriptions"
          element={
            <ProtectedRoute>
              <Subscriptions />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="admin/users"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <UnifiedUserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/service-users"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <ServiceUsersPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="admin/alerts/config" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <AlertsConfigPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/users/:id" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <UnifiedUserProfilePage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="admin/unified-users" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <UnifiedUserManagement />
            </ProtectedRoute>
          } 
        />

        <Route
          path="admin/roles"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <AdminRolesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/access-requests"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <AdminAccessRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/activity-logs"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <ActivityLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/audit-logs"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />

        <Route 
          path="admin/shortcuts" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <AdminShortcutsPage />
            </ProtectedRoute>
          } 
        />

        <Route
          path="admin/kiosks"
          element={
            <ProtectedRoute requiredPermission="kiosks:admin">
              <KiosksAdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="admin/menu"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <AdminMenuPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="admin/help/*"
          element={
            <ProtectedRoute requiredPermission="help:admin">
              <HelpAdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/ocr-monitoring" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'financial-manager']}>
              <OcrMonitoringPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/ocr-training" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <OcrTrainingPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="admin/finance-categories"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FinanceCategoriesAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/finance-accounts"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FinanceAccountsAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/status"
          element={
            <ProtectedRoute requiredPermission="systemStatus:read">
              <SystemStatusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/api-debug"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <ApiDebugPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/email-debug"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <EmailDebugPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="admin/code-console"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <CodeConsolePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="admin/integration-sync" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <IntegrationSyncManagement />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="admin/system-monitoring" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <SystemMonitoring />
            </ProtectedRoute>
          } 
        />


        {/* The page moved to /buildings. These stay as redirects so bookmarks,
            the older /admin/building-management links and the ?tab= redirects
            below all still land somewhere real. */}
        <Route
          path="admin/bms/*"
          element={<Navigate to="/buildings" replace />}
        />
        <Route
          path="admin/building-management/*"
          element={<Navigate to="/buildings" replace />}
        />

        <Route 
          path="admin/provisioning" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <ProvisioningManagementPage />
            </ProtectedRoute>
          } 
        />

        <Route
          path="admin/amazon"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <AmazonAdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="admin/search-index"
          element={
            <ProtectedRoute requiredPermission="search:admin">
              <SearchIndexPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="admin/database-migration"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <DatabaseMigrationPage />
            </ProtectedRoute>
          }
        />

        {/* BMS Admin routes - redirect to unified building management page */}
        <Route
          path="admin/utilities"
          element={<Navigate to="/buildings" replace />}
        />

        {/* Integration routes */}
        <Route 
          path="canva" 
          element={
            <ProtectedRoute>
              <CanvaPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="canva/setup" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <CanvaSetup />
            </ProtectedRoute>
          } 
        />



        <Route 
          path="planning-center/*" 
          element={
            <ProtectedRoute>
              <PlanningCenterPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="synology" 
          element={
            <ProtectedRoute>
              <SynologyFileBrowser />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="synology/setup" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <SynologySetup />
            </ProtectedRoute>
          } 
        />

        {/* Dreo routes */}
        <Route 
          path="dreo" 
          element={
            <ProtectedRoute>
              <DreoPage />
            </ProtectedRoute>
          } 
        />

        {/* LG ThinQ routes */}
        <Route 
          path="lg-thinq" 
          element={
            <ProtectedRoute>
              <LGThinqPage />
            </ProtectedRoute>
          } 
        />

        {/* Rain Bird routes */}
        <Route 
          path="rain-bird" 
          element={
            <ProtectedRoute>
              <RainBirdPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="rain-bird/setup" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <RainBirdSetup />
            </ProtectedRoute>
          } 
        />

        {/* Amazon routes */}
        <Route
          path="amazon"
          element={
            <ProtectedRoute>
              <AmazonPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="amazon/:tab"
          element={
            <ProtectedRoute>
              <AmazonPage />
            </ProtectedRoute>
          }
        />

        {/* QuickBooks routes */}
        <Route
          path="quickbooks/*"
          element={
            <ProtectedRoute requiredPermission="quickbooks:read">
              <QuickBooksPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="receipt-reconciliation"
          element={
            <ProtectedRoute requiredPermission="quickbooks:reconcile">
              <ReceiptReconciliationPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="transactions"
          element={
            <ProtectedRoute requiredPermission="quickbooks:reconcile">
              <TransactionsPage />
            </ProtectedRoute>
          }
        />

        {/* Lenel S2 NetBox routes */}
        <Route
          path="lenel-s2-netbox"
          element={
            <ProtectedRoute>
              <LenelS2NetBoxPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="lenel-s2-netbox/:tab"
          element={
            <ProtectedRoute>
              <LenelS2NetBoxPage />
            </ProtectedRoute>
          }
        />

        {/* Door Schedules (staff-facing unlock events) */}
        <Route
          path="door-schedules"
          element={
            <ProtectedRoute requiredPermission="accessControl:schedules:read">
              <DoorSchedulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="door-schedules/:tab"
          element={
            <ProtectedRoute requiredPermission="accessControl:schedules:read">
              <DoorSchedulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="door-schedules/people/bulk-add"
          element={
            <ProtectedRoute requiredPermission="accessControl:users:write">
              <BulkAddPeoplePage />
            </ProtectedRoute>
          }
        />

        {/* Cloudflare routes */}
        <Route
          path="cloudflare"
          element={
            <ProtectedRoute requiredPermission="cloudflare:read">
              <CloudflarePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="cloudflare/:tab"
          element={
            <ProtectedRoute requiredPermission="cloudflare:read">
              <CloudflarePage />
            </ProtectedRoute>
          }
        />

        {/* Mosyle Business routes */}
        <Route 
          path="mosyle-business" 
          element={
            <ProtectedRoute>
              <MosyleBusinessPage />
            </ProtectedRoute>
          } 
        />

        {/* UniFi Access routes */}
        <Route
          path="unifi-access"
          element={
            <ProtectedRoute>
              <UnifiAccessPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="unifi-access/:tab"
          element={
            <ProtectedRoute>
              <UnifiAccessPage />
            </ProtectedRoute>
          }
        />

        {/* UniFi Network routes */}
        <Route 
          path="unifi-network" 
          element={
            <ProtectedRoute>
              <UnifiNetworkPage />
            </ProtectedRoute>
          } 
        />

        {/* UniFi Protect routes */}
        <Route
          path="unifi-protect"
          element={
            <ProtectedRoute>
              <UnifiProtectPage />
            </ProtectedRoute>
          }
        />

        {/* UniFi Superlink routes */}
        <Route
          path="unifi-superlink"
          element={
            <ProtectedRoute>
              <UnifiSuperlinkPage />
            </ProtectedRoute>
          }
        />

        {/* TOTP Tokens route */}
        <Route 
          path="totp-tokens" 
          element={
            <ProtectedRoute>
              <TotpTokensPage />
            </ProtectedRoute>
          } 
        />

        {/* Access Control routes */}
        <Route
          path="unified-access-control"
          element={
            <ProtectedRoute requiredPermission="accessControl:admin">
              <UnifiedAccessControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="unified-access-control/:tab"
          element={
            <ProtectedRoute requiredPermission="accessControl:admin">
              <UnifiedAccessControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="access-control"
          element={
            <ProtectedRoute requiredPermission="accessControl:admin">
              <AccessControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="access-control/:tab"
          element={
            <ProtectedRoute requiredPermission="accessControl:admin">
              <AccessControlPage />
            </ProtectedRoute>
          }
        />

        {/* UniFi Access Users route */}
        <Route
          path="unifi-access-users"
          element={
            <ProtectedRoute requiredPermission="unifiAccess:read">
              <UnifiAccessUsersPage />
            </ProtectedRoute>
          }
        />

        {/* UniFi Access Logs route */}
        <Route
          path="unifi-access-logs"
          element={
            <ProtectedRoute requiredPermission="unifiAccess:read">
              <UnifiAccessLogsPage />
            </ProtectedRoute>
          }
        />

        {/* Google Admin routes */}
        <Route 
          path="google-admin" 
          element={
            <ProtectedRoute requiredRoles={['admin']} requiredPermission="googleAdmin:read">
              <GoogleAdminPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="google-admin/config" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <GoogleConfigEnvMessage serviceName="Google Admin" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="google-admin/users" 
          element={
            <ProtectedRoute requiredRoles={['admin']} requiredPermission="googleAdmin:read">
              <GoogleAdminUsersPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="google-admin/groups" 
          element={
            <ProtectedRoute requiredRoles={['admin']} requiredPermission="googleAdmin:read">
              <GoogleAdminGroupsPage />
            </ProtectedRoute>
          } 
        />

        {/* Google Calendar routes */}
        <Route 
          path="google-calendar" 
          element={
            <ProtectedRoute requiredPermission="googleCalendar:read">
              <GoogleCalendarPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="google-calendar/config" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <GoogleConfigEnvMessage serviceName="Google Calendar" />
            </ProtectedRoute>
          } 
        />

        {/* Google Forms routes */}
        <Route 
          path="google-forms" 
          element={
            <ProtectedRoute requiredPermission="googleForms:read">
              <GoogleFormsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="google-forms/config" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <GoogleConfigEnvMessage serviceName="Google Forms" />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="google-forms/webhooks/create" 
          element={
            <ProtectedRoute>
              <GoogleFormsWebhookPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="google-forms/webhooks/:id" 
          element={
            <ProtectedRoute>
              <GoogleFormsWebhookPage />
            </ProtectedRoute>
          } 
        />


        {/* Apple Business Manager routes */}
        <Route 
          path="apple-business-manager"
          element={
            <ProtectedRoute>
              <AppleBusinessManagerPage />
            </ProtectedRoute>
          }
        />

        {/* Building Management routes (redirect to unified BMS page) */}
        <Route
          path="building-management/*"
          element={
            <ProtectedRoute>
              <RedirectToBMS />
            </ProtectedRoute>
          }
        />

        {/* BMS (Building Management System) routes */}
        <Route
          path="bms/*"
          element={
            <ProtectedRoute>
              <BMSPage />
            </ProtectedRoute>
          }
        />

        {/* Parking Pass Management route */}
        <Route
          path="parking-passes"
          element={
            <ProtectedRoute>
              <ParkingPassManagement />
            </ProtectedRoute>
          }
        />

        {/* Phone Book route */}
        <Route 
          path="phone-book" 
          element={
            <ProtectedRoute>
              <PhoneBookPage />
            </ProtectedRoute>
          } 
        />

        {/* RADIUS Server routes */}
        <Route
          path="radius"
          element={<Navigate to="/radius/clients" replace />}
        />
        <Route
          path="radius/clients"
          element={
            <ProtectedRoute>
              <RadiusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="radius/vlans"
          element={
            <ProtectedRoute>
              <RadiusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="radius/group-vlans"
          element={
            <ProtectedRoute>
              <RadiusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="radius/user-vlans"
          element={
            <ProtectedRoute>
              <RadiusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="radius/standalone-logins"
          element={
            <ProtectedRoute>
              <RadiusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="radius/logs"
          element={
            <ProtectedRoute>
              <RadiusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="radius/setup"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <RadiusSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="radius/certificates"
          element={
            <ProtectedRoute>
              <CertificateManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/radius"
          element={<Navigate to="/radius/clients" replace />}
        />

        {/* WiiM Media Hub routes */}
        <Route
          path="wiim/setup"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <WiimSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="wiim/*"
          element={
            <ProtectedRoute>
              <WiimPage />
            </ProtectedRoute>
          }
        />

        {/* Print Server routes */}
        <Route
          path="print-server"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <PrintServerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="printers"
          element={
            <ProtectedRoute>
              <PrintersPage />
            </ProtectedRoute>
          }
        />

        {/* SkyportCloud HVAC Controls route */}
        <Route 
          path="skyportcloud" 
          element={
            <ProtectedRoute>
              <SkyportCloudPage />
            </ProtectedRoute>
          } 
        />

        {/* Q-sys route — supports sub-paths for each tab (e.g. /qsys/uci-viewer?uci=Chapel) */}
        <Route
          path="qsys/*"
          element={
            <ProtectedRoute>
              <QsysPage />
            </ProtectedRoute>
          }
        />

        {/* CueServer routes */}
        <Route
          path="cueserver"
          element={
            <ProtectedRoute>
              <CueServerPage />
            </ProtectedRoute>
          }
        />

        {/* Blackmagic Design routes */}
        <Route
          path="blackmagic"
          element={
            <ProtectedRoute>
              <BlackmagicPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="blackmagic/setup"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <BlackmagicSetup />
            </ProtectedRoute>
          }
        />

        <Route
          path="propresenter"
          element={
            <ProtectedRoute>
              <ProPresenterPage />
            </ProtectedRoute>
          }
        />

        {/* Unified media devices route — one page over every brand of TV, streamer and console */}
        <Route
          path="media-devices"
          element={
            <ProtectedRoute>
              <TVsPage />
            </ProtectedRoute>
          }
        />
        {/* A tab per URL, so a refresh lands back where it was */}
        <Route
          path="media-devices/:tab"
          element={
            <ProtectedRoute>
              <TVsPage />
            </ProtectedRoute>
          }
        />
        {/* Was /tvs before consoles joined the page; bookmarks still work. */}
        <Route path="tvs" element={<Navigate to="/media-devices" replace />} />

        {/* Unified Projectors route */}
        <Route
          path="projectors"
          element={
            <ProtectedRoute>
              <ProjectorsPage />
            </ProtectedRoute>
          }
        />
        {/* Redirects from old projector routes */}
        <Route path="epson" element={<Navigate to="/projectors" replace />} />
        <Route path="epson/setup" element={<Navigate to="/projectors" replace />} />
        <Route path="panasonic-projector" element={<Navigate to="/projectors" replace />} />

        {/* Production Dashboard route */}
        <Route
          path="production"
          element={
            <ProtectedRoute>
              <ProductionDashboard />
            </ProtectedRoute>
          }
        />

        {/* LEA Professional speaker zones */}
        <Route
          path="speaker-zones"
          element={
            <ProtectedRoute>
              <SpeakerZonesPage />
            </ProtectedRoute>
          }
        />

        {/* Yamaha TF Rack route */}
        <Route
          path="yamaha-tf"
          element={
            <ProtectedRoute>
              <YamahaTfPage />
            </ProtectedRoute>
          }
        />

        {/* Power distribution (RackLink) */}
        <Route
          path="power"
          element={
            <ProtectedRoute requiredPermission="power:read">
              <PowerPage />
            </ProtectedRoute>
          }
        />

        {/* Canon PTZ Camera route */}
        <Route
          path="canon"
          element={
            <ProtectedRoute>
              <CanonPage />
            </ProtectedRoute>
          }
        />

        {/* Cisco Switch Monitoring route */}
        <Route
          path="cisco"
          element={
            <ProtectedRoute requiredPermission="cisco:read">
              <CiscoSwitchPage />
            </ProtectedRoute>
          }
        />

        {/* Govee Smart Devices route */}
        <Route 
          path="govee" 
          element={
            <ProtectedRoute>
              <GoveePage />
            </ProtectedRoute>
          } 
        />

        {/* Constant Contact route */}
        <Route
          path="constant-contact"
          element={
            <ProtectedRoute>
              <ConstantContactPage />
            </ProtectedRoute>
          }
        />

        {/* People Directory routes */}
        <Route 
          path="people/*" 
          element={
            <ProtectedRoute>
              <UnifiedPeoplePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="team-hubs" 
          element={
            <ProtectedRoute>
              <TeamHubsIndexPage />
            </ProtectedRoute>
          } 
        />

        {/* Help and FAQ routes */}
        <Route 
          path="help" 
          element={
            <ProtectedRoute>
              <HelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/faq" 
          element={
            <ProtectedRoute>
              <FAQPage />
            </ProtectedRoute>
          } 
        />

        {/* Getting Started help routes */}
        <Route 
          path="help/logging-in" 
          element={
            <ProtectedRoute>
              <LoggingInPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/navigation" 
          element={
            <ProtectedRoute>
              <NavigationPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/your-profile" 
          element={
            <ProtectedRoute>
              <YourProfilePage />
            </ProtectedRoute>
          } 
        />

        {/* Core Features help routes */}
        <Route 
          path="help/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/shortcuts" 
          element={
            <ProtectedRoute>
              <ShortcutsHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/drive-files" 
          element={
            <ProtectedRoute>
              <DriveFilesHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/calendar" 
          element={
            <ProtectedRoute>
              <CalendarHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/staff-directory" 
          element={
            <ProtectedRoute>
              <StaffDirectoryHelpPage />
            </ProtectedRoute>
          } 
        />

        {/* Integrations help routes */}
        <Route 
          path="help/canva" 
          element={
            <ProtectedRoute>
              <CanvaHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/planning-center" 
          element={
            <ProtectedRoute>
              <PlanningCenterHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/synology" 
          element={
            <ProtectedRoute>
              <SynologyHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/dreo" 
          element={
            <ProtectedRoute>
              <DreoHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/unifi-access" 
          element={
            <ProtectedRoute>
              <UnifiAccessHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/google-drive" 
          element={
            <ProtectedRoute>
              <GoogleDriveHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/google-calendar" 
          element={
            <ProtectedRoute>
              <GoogleCalendarHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/google-forms" 
          element={
            <ProtectedRoute>
              <GoogleFormsHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/google-admin" 
          element={
            <ProtectedRoute>
              <GoogleAdminHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/lenel-s2-netbox" 
          element={
            <ProtectedRoute>
              <LenelS2NetBoxHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/mosyle-business" 
          element={
            <ProtectedRoute>
              <MosyleBusinessHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/unifi-network" 
          element={
            <ProtectedRoute>
              <UnifiNetworkHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/unifi-protect" 
          element={
            <ProtectedRoute>
              <UnifiProtectHelpPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="help/radius" 
          element={
            <ProtectedRoute>
              <RadiusHelpPage />
            </ProtectedRoute>
          } 
        />

        {/* Assets routes */}
        <Route
          path="assets"
          element={
            <ProtectedRoute>
              <AssetDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/requests"
          element={
            <ProtectedRoute>
              <AssetRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/issues"
          element={
            <ProtectedRoute>
              <AssetIssuesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/list"
          element={
            <ProtectedRoute>
              <AssetListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/categories"
          element={
            <ProtectedRoute>
              <AssetListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/locations"
          element={
            <ProtectedRoute>
              <AssetListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/new"
          element={
            <ProtectedRoute requiredPermission="assets:write">
              <AssetFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/reports"
          element={
            <ProtectedRoute>
              <AssetReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/import-export"
          element={
            <ProtectedRoute>
              <AssetImportExportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/:id/edit"
          element={
            <ProtectedRoute requiredPermission="assets:write">
              <AssetFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/:id/history"
          element={
            <ProtectedRoute>
              <AssetHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="assets/:id"
          element={
            <ProtectedRoute requiredPermission="assets:read">
              <AssetViewPage />
            </ProtectedRoute>
          }
        />
        {/* Legacy asset management reports route redirect */}
        <Route 
          path="asset-management/reports" 
          element={<Navigate to="/assets/reports" />}
        />

        {/* Notes routes */}
        <Route 
          path="notes" 
          element={
            <ProtectedRoute>
              <NotesPage />
            </ProtectedRoute>
          } 
        />

        {/* Receipt Management routes */}
        <Route
          path="receipts"
          element={
            <ProtectedRoute>
              <ReceiptManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="receipts/:tab"
          element={
            <ProtectedRoute>
              <ReceiptManagementPage />
            </ProtectedRoute>
          }
        />

d        {/* Purchase Request routes moved to shopping/requests */}
        <Route
          path="purchase-requests/*"
          element={<Navigate to="/shopping/requests" replace />}
        />
        <Route
          path="procurement/requests/*"
          element={<Navigate to="/shopping/requests" replace />}
        />

        <Route
          path="shopping/requests/*"
          element={
            <ProtectedRoute>
              <PurchaseRequestPage />
            </ProtectedRoute>
          }
        />

        {/* License Manager routes */}
        <Route
          path="licenses"
          element={
            <ProtectedRoute>
              <LicenseManagerPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<LicenseBrowser />} />
          <Route path="my-licenses" element={<MyLicenses />} />
          <Route path="requests" element={<LicenseRequests />} />
          <Route path="admin" element={<AdminLicenseManagement />}>
            <Route index element={null} />
            <Route path="licenses" element={null} />
            <Route path="requests" element={null} />
            <Route path="reclamation" element={null} />
          </Route>
        </Route>

        {/* Procurement System routes */}
        <Route
          path="procurement"
          element={
            <ProtectedRoute requiredPermission="purchaseRequests:read">
              <ProcurementDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="procurement/orders"
          element={
            <ProtectedRoute requiredPermission="purchaseRequests:read">
              <OrderManagement />
            </ProtectedRoute>
          }
        />
        {/* Legacy procurement vendor route redirect */}
        <Route
          path="procurement/vendors"
          element={<Navigate to="/shopping/vendors" replace />}
        />

        {/* Procurement Analytics */}
        <Route
          path="procurement/analytics"
          element={
            <ProtectedRoute requiredPermission="purchaseRequests:read">
              <ProcurementAnalytics />
            </ProtectedRoute>
          }
        />

        {/* Purchasing Groups */}
        <Route
          path="procurement/groups"
          element={
            <ProtectedRoute requiredPermission="purchaseRequests:read">
              <PurchasingGroups />
            </ProtectedRoute>
          }
        />



        {/* Document Collaboration */}
        <Route
          path="procurement/documents"
          element={
            <ProtectedRoute requiredPermission="purchaseRequests:read">
              <DocumentCollaboration />
            </ProtectedRoute>
          }
        />


        {/* Shopping routes matching menu URLs */}
        <Route
          path="shopping/amazon"
          element={
            <ProtectedRoute requiredPermission="purchaseRequests:read">
              <AmazonShopping />
            </ProtectedRoute>
          }
        />
        <Route
          path="shopping/vendors"
          element={
            <ProtectedRoute requiredPermission="purchaseRequests:read">
              <VendorManagement />
            </ProtectedRoute>
          }
        />

        {/* Legacy procurement Amazon shopping route redirect */}
        <Route
          path="procurement/shop/amazon"
          element={<Navigate to="/shopping/amazon" replace />}
        />

        {/* Admin Procurement routes */}
        <Route
          path="admin/procurement"
          element={
            <ProtectedRoute requiredRoles={['admin']} requiredPermission="purchaseRequests:admin">
              <ProcurementSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/amazon-punchout"
          element={
            <ProtectedRoute requiredRoles={['admin']} requiredPermission="purchaseRequests:admin">
              <AmazonPunchoutConfig />
            </ProtectedRoute>
          }
        />

        {/* News routes */}
        <Route 
          path="news" 
          element={
            <ProtectedRoute>
              <NewsPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="news/posts/new"
          element={
            <ProtectedRoute>
              <NewsPostForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="news/posts/:id/edit"
          element={
            <ProtectedRoute>
              <NewsPostForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="news/posts/:id"
          element={
            <ProtectedRoute>
              <NewsPostPage />
            </ProtectedRoute>
          }
        />

        {/* Meeting Management routes */}
        <Route
          path="meetings"
          element={<Navigate to="/meetings/upcoming" replace />}
        />
        <Route
          path="meetings/upcoming"
          element={
            <ProtectedRoute>
              <MeetingsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/past"
          element={
            <ProtectedRoute>
              <MeetingsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/all"
          element={
            <ProtectedRoute>
              <MeetingsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/new"
          element={
            <ProtectedRoute>
              <MeetingFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/committees"
          element={
            <ProtectedRoute>
              <CommitteesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/committees/new"
          element={
            <ProtectedRoute>
              <CommitteeFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/committees/:id/edit"
          element={
            <ProtectedRoute>
              <CommitteeFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/committees/:id"
          element={
            <ProtectedRoute>
              <CommitteeDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/:id/edit"
          element={
            <ProtectedRoute>
              <MeetingFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/:id/agenda"
          element={
            <ProtectedRoute>
              <AgendaBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="meetings/:id"
          element={
            <ProtectedRoute>
              <MeetingDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Form Builder routes */}
        <Route
          path="forms"
          element={
            <ProtectedRoute>
              <FormsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="forms/my-forms"
          element={
            <ProtectedRoute>
              <MyFormsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="forms/new"
          element={
            <ProtectedRoute>
              <FormBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="forms/edit/:id"
          element={
            <ProtectedRoute>
              <FormBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="forms/:formId/submissions"
          element={
            <ProtectedRoute>
              <FormSubmissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="f/:slug"
          element={
            <FormViewPage />
          }
        />

        {/* Staff Directory routes - specific routes must come before wildcard */}
        <Route
          path="staff-directory/users/:id"
          element={
            <ProtectedRoute>
              <UserProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff-directory/teams/new"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <TeamManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff-directory/teams/:id"
          element={
            <ProtectedRoute>
              <TeamManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff-directory/groups/new"
          element={
            <ProtectedRoute>
              <GroupManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff-directory/groups/:id"
          element={
            <ProtectedRoute>
              <GroupManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff-directory/*"
          element={
            <ProtectedRoute>
              <StaffDirectoryPage />
            </ProtectedRoute>
          }
        />

        {/* Team Management routes */}
        <Route
          path="teams"
          element={
            <ProtectedRoute>
              <TeamsHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="teams/new"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <TeamManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="teams/:teamId/help"
          element={
            <ProtectedRoute>
              <TeamHelpManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="teams/:teamId/hub"
          element={
            <ProtectedRoute>
              <TeamHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="teams/:id"
          element={
            <ProtectedRoute>
              <TeamManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Ticket Management routes */}
        <Route 
          path="tickets" 
          element={
            <ProtectedRoute>
              <TicketsPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="tickets/new"
          element={
            <ProtectedRoute>
              <TicketFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tickets/:id/edit"
          element={
            <ProtectedRoute>
              <TicketFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tickets/:id"
          element={
            <ProtectedRoute>
              <TicketDetailPage />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Ticket Management routes */}
        <Route 
          path="admin/ticket-categories-and-tags" 
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <TicketCategoriesAndTagsPage />
            </ProtectedRoute>
          } 
        />
        {/* Redirect old routes to new combined page */}
        <Route 
          path="admin/ticket-categories" 
          element={<Navigate to="/admin/ticket-categories-and-tags" />} 
        />
        <Route 
          path="admin/ticket-tags" 
          element={<Navigate to="/admin/ticket-categories-and-tags" />} 
        />

        {/* Task Management routes - specific routes must come before wildcard */}
        <Route
          path="tasks/new"
          element={
            <ProtectedRoute>
              <TaskFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks/maintenance/new"
          element={
            <ProtectedRoute>
              <MaintenanceTaskFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks/recurring"
          element={
            <ProtectedRoute>
              <RecurringTasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks/:id/edit"
          element={
            <ProtectedRoute>
              <TaskFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks/:id"
          element={
            <ProtectedRoute>
              <TaskDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks/*"
          element={
            <ProtectedRoute>
              <TasksPage />
            </ProtectedRoute>
          }
        />

        {/* Team Hub routes */}
        <Route
          path="team-hub/:teamId/hub"
          element={
            <ProtectedRoute>
              <TeamHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="team-hub/:teamId/help"
          element={
            <ProtectedRoute>
              <TeamHelpManagement />
            </ProtectedRoute>
          }
        />

        {/* 404 route */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      </Routes>
      </Suspense>
    </LazyLoadErrorBoundary>
  );

  return (
    <EnvironmentWrapper
      debugComponent={
        <DebugProvider>
          <ErrorBoundary>
            <AppContent />
            <DebugWidget />
          </ErrorBoundary>
        </DebugProvider>
      }
      productionComponent={
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      }
    >
      {/* This wrapper handles the conditional rendering based on environment */}
    </EnvironmentWrapper>
  );
}

export default App;
